import React, { useState, useRef } from "react";
import { styled } from "@mui/material/styles";
import {
  Grid,
  Box,
  Menu,
  FormControlLabel,
  Checkbox,
  IconButton,
} from "@mui/material";

import { SolidButton } from "components/buttons/SolidButton";
import { OutlineButton } from "components/buttons/OutlineButton";

import { ReactComponent as SelectedIcon } from "assets/images/icons/icn_selected.svg";
import { ReactComponent as UnselectedIcon } from "assets/images/icons/icn_unselected.svg";
import icon_options from "assets/images/icons/icn_display_options.svg";
import { ReactComponent as LockIcon } from "assets/images/icons/icn_table_cell_Lock_Columns.svg";
import { ReactComponent as UnLockIcon } from "assets/images/icons/icn_table_cell_UnLock_Columns.svg";

const BtnIcon = styled(Box)(() => ({
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "0px 8px"
}));

const GroupTitle = styled(Box)(({ theme }) => ({
  fontSize: theme.typography.customFontSize[2],
  fontWeight: theme.typography.customFontWeight[3],
  color: theme.palette.primary.greyPrimary,
}));

const SpecialColumn = styled(Box)(({ theme }) => ({
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  fontSize: theme.typography.customFontSize[2],
}));

const CusMenuCol = styled(Grid, {
  shouldForwardProp: (prop) => prop !== "isSpecial",
})(({ theme, width, isSpecial }) => ({
  width: width,
  margin: "3px 0",
  borderRadius: theme.shape.customBorderRadius[2],
  alignItems: "center",
  justifyContent: "space-between",
  ...(isSpecial
    ? {}
    : {
        "&:hover": {
          backgroundColor: theme.palette.primary.subBlue,
        },
      }),
}));

const StyledMenu = styled(
  (props) => (
    <Menu
      elevation={0}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      {...props}
    />
  ),
  {
    shouldForwardProp: (prop) => prop !== "maxWidth",
  }
)(({ theme, maxWidth }) => ({
  "& .MuiPaper-root": {
    marginTop: theme.spacing(2),
    marginLeft: theme.spacing(4),
    borderRadius: theme.shape.customBorderRadius[3],
    backgroundColor: theme.palette.primary.white,
    maxWidth: maxWidth || 450,
    height: "auto",
    border: `1px solid ${theme.palette.primary.whiteSecound}`,
    position: "relative",
    overflow: "auto",
    boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
    "&::before": {
      content: '""',
      position: "absolute",
      top: "-12px",
      right: "34px",
      width: 0,
      height: 0,
      borderLeft: "8px solid transparent",
      borderRight: "8px solid transparent",
      borderBottom: `12px solid ${theme.palette.primary.white}`,
      filter:
        "drop-shadow(-2px -1px 2px rgba(0, 0, 0, 0.05)) drop-shadow(2px -1px 2px rgba(0, 0, 0, 0.05))",
      zIndex: 1,
    },
  },
}));

const StyledFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
  ".MuiFormControlLabel-label": {
    fontSize: theme.typography.customFontSize[2],
    color: theme.palette.primary.greyPrimary,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
}));

const GroupMenu = ({ table, groupedData, hiddenCheck }) => (
  <Box sx={{ flex: 1, overflowY: "auto", px: 2, pt: 1 }}>
    <Box sx={{ display: "flex", flexDirection: "row", gap: 4 }}>
      {groupedData.map((group, groupIndex) => (
        <Box key={groupIndex} sx={{ flex: 1 }}>
          {group.groupNames.map((groupTitle) => {
            const columnIds = group.columns[groupTitle];
            if (!columnIds) return null;

            return (
              <Box key={groupTitle} sx={{ mb: 2, display: "flex", flexDirection: "column" }}>
                {groupTitle && <GroupTitle sx={{ height: 24 }}>{groupTitle}</GroupTitle> }
                {columnIds.map((columnId) => {
                  const column = table.getColumn(columnId);
                  if (!column) return null;

                  const isSpecialColumn = hiddenCheck && hiddenCheck.includes(column.id);

                  return (
                    <CusMenuCol
                      container
                      width="100%"
                      isSpecial={isSpecialColumn}
                      key={column.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        minHeight: 40,
                        py: 0.5,
                      }}
                    >
                      {isSpecialColumn ? (
                        <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                          <Checkbox disabled sx={{ visibility: "hidden" }} />
                          <SpecialColumn>{column.columnDef.header}</SpecialColumn>
                        </Box>
                      ) : (
                        <StyledFormControlLabel
                          sx={{ m: 0, width: "100%" }}
                          control={
                            <Checkbox
                              checked={column.getIsVisible()}
                              onChange={column.getToggleVisibilityHandler()}
                              name={column.id}
                            />
                          }
                          label={column.columnDef.header}
                        />
                      )}
                    </CusMenuCol>
                  );
                })}
              </Box>
            );
          })}
        </Box>
      ))}
    </Box>
  </Box>
);

const LockPinningMenu = ({ table, initialLeafColumns }) => (
  <Box
    sx={{
      flex: 1,
      overflowY: "auto",
      px: 2,
      pt: 1,
      display: "flex",
      flexWrap: "wrap",
    }}
  >
    {initialLeafColumns.current
      .filter((columnId) => columnId !== "select")
      .map((columnId) => {
        const column = table.getColumn(columnId);
        if (!column) return null;

        return (
          <CusMenuCol container width="50%" key={column.id}>
            <Grid item xs={10}>
              <StyledFormControlLabel
                sx={{ mt: 0.3 }}
                control={
                  <Checkbox
                    icon={<UnselectedIcon style={{ width: "44px" }} />}
                    checkedIcon={<SelectedIcon style={{ width: "44px" }} />}
                    checked={column.getIsVisible()}
                    onChange={column.getToggleVisibilityHandler()}
                    name={column.id}
                  />
                }
                label={column.columnDef.header}
              />
            </Grid>
            {column.columnDef.enableColumnPinning && (
              <Grid
                item
                xs={2}
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => {
                    if (column.getIsPinned()) {
                      column.pin(false);
                    } else {
                      column.pin("left");
                    }
                  }}
                  sx={{
                    ml: "auto",
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  {column.getIsPinned() ? (
                    <LockIcon fontSize="small" />
                  ) : (
                    <UnLockIcon fontSize="small" />
                  )}
                </IconButton>
              </Grid>
            )}
          </CusMenuCol>
        );
      })}
  </Box>
);

const ColumnVisibilityToggle = ({
  table,
  maxWidth,
  groupedData = {},
  hiddenCheck,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const allColumnIds = table?.getAllLeafColumns().map((col) => col.id) ?? [];
  const initialLeafColumns = useRef(
    table?.getAllLeafColumns().map((col) => col.id) ?? []
  );

  if (!table) return null;

  function handleSelectAll() {
    const visibilityState = {};
    allColumnIds.forEach((id) => {
      visibilityState[id] = true;
    });
    table.setColumnVisibility(visibilityState);
  }

  function handleReset() {
    const initialVisibility = table.initialState?.columnVisibility ?? {};
    const visibilityState = {};
    allColumnIds.forEach((id) => {
      visibilityState[id] = initialVisibility[id] !== false;
    });
    table.setColumnVisibility(visibilityState);
    table.resetColumnPinning();
  }

  return (
    <>
      <BtnIcon onClick={(e) => setAnchorEl(e.currentTarget)}>
        <img src={icon_options} alt="table options" />
      </BtnIcon>
      <StyledMenu
        maxWidth={maxWidth}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorEl={anchorEl}
      >
        {Object.keys(groupedData).length > 0 ? (
          <GroupMenu
            table={table}
            groupedData={groupedData}
            hiddenCheck={hiddenCheck}
          />
        ) : (
          <LockPinningMenu
            table={table}
            initialLeafColumns={initialLeafColumns}
          />
        )}
        <Box display="flex" justifyContent="center" gap={2} py={1.5}>
          <OutlineButton width="96px" height="32px" onClick={handleReset}>
            Reset
          </OutlineButton>
          <SolidButton width="96px" height="32px" onClick={handleSelectAll}>
            Show All
          </SolidButton>
        </Box>
      </StyledMenu>
    </>
  );
};

export default ColumnVisibilityToggle;
